import { nanoid } from 'nanoid';
import dayjs from 'dayjs';
import { getFingerPrint } from './finger';
import { getDuration } from './utils';
const SESSION_INFO_KEY = 'point_session_info';

let windowDataLayerName = '';
let startTime = new Date();
let needSendSessionEnd = false;
let memorySessionInfo = {};
let storageWriteAvailable = true;
/** 进行中的单次 session 初始化 Promise */
let sessionInitPromise = null;
/** 进行中的 ensureSessionInfo（含重试）Promise，并发上报共用 */
let ensureSessionPromise = null;
/** sid 已写入但 session_start 因 dataLayer 未就绪而未发出 */
let pendingSessionStartIsFirst = null;

const SESSION_INIT_MAX_RETRY = 3;
const SESSION_INIT_RETRY_DELAY_MS = 200;
/** 重试耗尽仍无 sid 时，上报使用的兜底 session_id（不写入本地存储） */
const FALLBACK_SESSION_ID = 'unknown_empty_session_id';
const FALLBACK_SESSION_INFO = {
  sid: FALLBACK_SESSION_ID,
  sount: 0,
  activeTime: ''
};

const delay = ms => new Promise(resolve => setTimeout(resolve, ms));

const logSessionError = (scope, reason, detail = {}) => {
  console.error(
    scope +
      ' error: ' +
      reason +
      ' ' +
      JSON.stringify({
        storageWriteAvailable,
        memoryHasSid: !!memorySessionInfo?.sid,
        ...detail
      })
  );
};

const pushSessionStart = eventIsFirst => {
  if (
    windowDataLayerName &&
    window[windowDataLayerName] &&
    typeof window[windowDataLayerName].push === 'function'
  ) {
    window[windowDataLayerName].push({
      eventName: 'session_start',
      eventIsFirst
    });
    pendingSessionStartIsFirst = null;
    return;
  }
  pendingSessionStartIsFirst = eventIsFirst;
};

const flushPendingSessionStart = () => {
  if (pendingSessionStartIsFirst == null) {
    return;
  }
  pushSessionStart(pendingSessionStartIsFirst);
};

const dealStorageAction = {
  // 首次初始化
  initSessionId: async () => {
    setSessionInfo({
      sid: await getNewSessionId(),
      sount: 1,
      activeTime: getNowTime()
    });
    startTime = new Date();
    pushSessionStart('1');
  },
  // 更新sessionId、count+1
  updateSessionId: async () => {
    const sessionInfo = getSessionInfo();
    setSessionInfo({
      sid: await getNewSessionId(),
      sount: sessionInfo?.sount + 1 || 1,
      activeTime: getNowTime()
    });
    needSendSessionEnd && sendSessionEndPoint();
    startTime = new Date();
    pushSessionStart('0');
  },
  // 仅更新时间
  updateTime: async () => {
    const sessionInfo = getSessionInfo();
    if (!sessionInfo?.sid) {
      await ensureSessionInfo();
    } else {
      setSessionInfo({
        sid: sessionInfo.sid,
        sount: sessionInfo?.sount || 1,
        activeTime: getNowTime()
      });
    }
  }
};

export const sendSessionEndPoint = () => {
  if (
    !windowDataLayerName ||
    !window[windowDataLayerName] ||
    typeof window[windowDataLayerName].push !== 'function'
  ) {
    logSessionError('sendSessionEndPoint', 'dataLayer not ready', {
      windowDataLayerName
    });
    return;
  }
  const duration = getDuration(startTime);
  window[windowDataLayerName].push({
    eventName: 'session_end',
    eventReason: window?.pointPageName || '',
    eventValue: {
      duration
    }
  });
};

export const handleSessionId = ({ dataLayerName, sendSessionEnd }) => {
  windowDataLayerName = dataLayerName;
  needSendSessionEnd = sendSessionEnd;
  initPageVisibilityChange();
  flushPendingSessionStart();
  // 有意 fire-and-forget：页面入口不阻塞；上报侧通过 ensureSessionInfo 等待 sid 就绪
  initSessionId();
};

const startSessionInit = () => {
  if (!sessionInitPromise) {
    sessionInitPromise = dealStorageAction
      .initSessionId()
      .catch(error => {
        logSessionError('ensureSessionInfo', 'init failed', {
          name: error?.name,
          message: error?.message
        });
      })
      .finally(() => {
        sessionInitPromise = null;
      });
  }
  return sessionInitPromise;
};

const getFallbackSessionInfo = () => {
  logSessionError('ensureSessionInfo', 'use fallback session id after retries exhausted', {
    fallbackSessionId: FALLBACK_SESSION_ID,
    maxRetry: SESSION_INIT_MAX_RETRY
  });
  return { ...FALLBACK_SESSION_INFO };
};

/** 单次初始化尝试：启动/等待 init，成功返回 sessionInfo，否则返回 null */
const tryInitSessionOnce = async attempt => {
  await startSessionInit();
  const sessionInfo = getSessionInfo();
  if (sessionInfo?.sid) {
    return sessionInfo;
  }
  logSessionError('ensureSessionInfo', 'session sid still empty after init', {
    attempt,
    maxRetry: SESSION_INIT_MAX_RETRY
  });
  return null;
};

/** 带退避的重试初始化；全部失败返回 null */
const initSessionWithRetry = async () => {
  for (let attempt = 1; attempt <= SESSION_INIT_MAX_RETRY; attempt++) {
    const sessionInfo = await tryInitSessionOnce(attempt);
    if (sessionInfo?.sid) {
      return sessionInfo;
    }
    if (attempt < SESSION_INIT_MAX_RETRY) {
      await delay(SESSION_INIT_RETRY_DELAY_MS * attempt);
    }
  }
  return null;
};

/**
 * 保证 session sid 已初始化完成后再返回 sessionInfo。
 * 上报前应 await 此方法，避免拿到空 session_id。
 * 初始化失败或仍无 sid 时最多重试 SESSION_INIT_MAX_RETRY 次；
 * 仍失败则返回固定兜底 sid（不写入本地存储，避免污染真实 session）。
 */
export const ensureSessionInfo = async () => {
  const current = getSessionInfo();
  if (current?.sid) {
    return current;
  }

  if (!ensureSessionPromise) {
    ensureSessionPromise = (async () => {
      const sessionInfo = await initSessionWithRetry();
      return sessionInfo?.sid ? sessionInfo : getFallbackSessionInfo();
    })().finally(() => {
      ensureSessionPromise = null;
    });
  }

  return ensureSessionPromise;
};

const initSessionId = async () => {
  // 若已有初始化在进行（例如上报提前触发了 ensureSessionInfo），先等它完成再决策
  if (sessionInitPromise) {
    await sessionInitPromise;
  }

  const sessionInfo = getSessionInfo();
  if (!sessionInfo?.sid) {
    await ensureSessionInfo();
    // ensure 内 init 可能因 dataLayer 未就绪而暂存了 session_start，此处补发
    flushPendingSessionStart();
    return;
  }
  flushPendingSessionStart();
  if (sessionInfo?.activeTime && checkOverMinute(sessionInfo.activeTime)) {
    await dealStorageAction.updateSessionId();
  } else {
    await dealStorageAction.updateTime();
  }
};

const getHiddenProperty = () => {
  if ('hidden' in document) {
    return 'hidden';
  }
  if ('webkitHidden' in document) {
    return 'webkitHidden';
  }
  if ('mozHidden' in document) {
    return 'mozHidden';
  }
  return null;
};

const initPageVisibilityChange = () => {
  const hiddenProperty = getHiddenProperty();
  if (!hiddenProperty) {
    logSessionError('initPageVisibilityChange', 'Page Visibility API not supported');
    return;
  }

  const visibilityChangeEvent = hiddenProperty.replace(
    /hidden/i,
    'visibilitychange'
  );
  const onVisibilityChange = function () {
    if (!document[hiddenProperty]) {
      // 与 handleSessionId 一致：可见性回调不阻塞，内部自行 await
      initSessionId();
    } else {
      dealStorageAction.updateTime();
    }
  };
  document.addEventListener(visibilityChangeEvent, onVisibilityChange);
};

export const getNewSessionId = async () => {
  const fingerTxt = await getFingerPrint();
  const idLength = 25;
  return nanoid(idLength) + '_' + fingerTxt;
};

const checkOverMinute = time => {
  return dayjs(new Date()).diff(dayjs(time), 'minute') > 30;
};

const getNowTime = () => {
  return dayjs(new Date()).format('YYYY-MM-DD HH:mm:ss');
};

export const getSessionInfo = () => {
  if (!storageWriteAvailable) {
    return memorySessionInfo?.sid ? memorySessionInfo : {};
  }

  try {
    const raw = localStorage.getItem(SESSION_INFO_KEY);
    if (!raw) {
      return {};
    }
    const sessionInfo = JSON.parse(raw);
    const result =
      sessionInfo && typeof sessionInfo === 'object' ? sessionInfo : {};
    if (!result?.sid) {
      logSessionError('getSessionInfo', 'invalid session data in localStorage', {
        rawLength: raw.length
      });
    }
    return result;
  } catch (e) {
    // 隐私模式下访问 localStorage 可能抛出 SecurityError；脏数据会导致 SyntaxError
    logSessionError('getSessionInfo', 'read localStorage failed', {
      name: e.name,
      message: e.message
    });
    if (e instanceof SyntaxError) {
      try {
        localStorage.removeItem(SESSION_INFO_KEY);
      } catch (removeError) {
        logSessionError('getSessionInfo', 'remove corrupted data failed', {
          name: removeError.name,
          message: removeError.message
        });
      }
    }
    return {};
  }
};

const setSessionInfo = ({ sid, sount, activeTime }) => {
  if (!sid) {
    logSessionError('setSessionInfo', 'sid is empty', { sount, activeTime });
    return;
  }

  const sessionData = { sid, sount, activeTime };

  if (!storageWriteAvailable) {
    memorySessionInfo = sessionData;
    return;
  }

  const serialized = JSON.stringify(sessionData);

  const useMemoryFallback = (reason, detail) => {
    // 有意设计：页面生命周期内一旦降级为内存，不再回写 localStorage
    storageWriteAvailable = false;
    memorySessionInfo = sessionData;
    try {
      localStorage.removeItem(SESSION_INFO_KEY);
    } catch (removeError) {
      // 清理失败不影响主流程（已写入 memorySessionInfo）
      logSessionError('setSessionInfo', 'removeItem after memory fallback failed', {
        name: removeError.name,
        message: removeError.message,
        fallbackReason: reason
      });
    }
    logSessionError('setSessionInfo', reason, detail);
  };

  try {
    localStorage.setItem(SESSION_INFO_KEY, serialized);
    const readBack = localStorage.getItem(SESSION_INFO_KEY);
    if (readBack !== serialized) {
      useMemoryFallback('storage write verify failed', {
        expectedLength: serialized.length,
        actualLength: readBack?.length ?? 0
      });
    }
  } catch (e) {
    useMemoryFallback('set localStorage error', { name: e.name, message: e.message });
  }
};
